import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { clearBrandingLogosCache } from "utils/branding/brandingCache";

const STORAGE_KEY = "impersonation";
const AUTO_EXIT_MS = 15 * 60 * 1000;

export type ImpersonationTarget = {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
  role: string | null;
  companyId: string;
  companyName: string | null;
  features?: string[] | null;
};

export type ImpersonationSession = {
  sessionId: string;
  startedAt: number;
  target: ImpersonationTarget;
};

type ImpersonationContextValue = {
  session: ImpersonationSession | null;
  startSession: (session: ImpersonationSession) => void;
  endSession: () => void;
};

const ImpersonationCtx = createContext<ImpersonationContextValue | null>(null);

function readFromStorage(): ImpersonationSession | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ImpersonationSession;
    if (!parsed?.sessionId || !parsed?.target?.id) return null;
    if (Date.now() - parsed.startedAt > AUTO_EXIT_MS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeToStorage(session: ImpersonationSession | null) {
  try {
    if (session) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // sessionStorage unavailable — fail silently
  }
}

export function getImpersonationFromStorage(): ImpersonationSession | null {
  return readFromStorage();
}

export const ImpersonationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [session, setSession] = useState<ImpersonationSession | null>(() =>
    readFromStorage(),
  );
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const endSession = useCallback(() => {
    writeToStorage(null);
    clearBrandingLogosCache();
    setSession(null);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startSession = useCallback((next: ImpersonationSession) => {
    writeToStorage(next);
    clearBrandingLogosCache();
    setSession(next);
  }, []);

  useEffect(() => {
    if (!session) return;
    const elapsed = Date.now() - session.startedAt;
    const remaining = Math.max(AUTO_EXIT_MS - elapsed, 0);
    timerRef.current = setTimeout(() => endSession(), remaining);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, [session, endSession]);

  const value = useMemo(
    () => ({ session, startSession, endSession }),
    [session, startSession, endSession],
  );

  return (
    <ImpersonationCtx.Provider value={value}>
      {children}
    </ImpersonationCtx.Provider>
  );
};

export function useImpersonation() {
  const ctx = useContext(ImpersonationCtx);
  if (!ctx) {
    throw new Error(
      "useImpersonation must be used within <ImpersonationProvider>",
    );
  }
  return ctx;
}
